import { Divider } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import SearchField from 'src/shared/components/form/searchField';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'src/shared/models/enums/gridFilterConfig';
import { GroupMessageSmsTabs } from '../components/groupMessageSmsTabs';
import { GroupMessageSmsSearchFilter } from '../models/groupMessageSmsSearchFilter';
import { GroupMessageSmsGrid } from '../components/groupMessageSmsGrid';

export const GroupMessageSmsOverviewPage = () => {
  const isMounted = useIsMounted();
  const [, setLoading] = useState<boolean>(false);
  const [tempSearchString, setTempSearchString] = useState<string | undefined>(undefined);
  const [searchInitialized, setSearchInitialized] = useState<boolean>(false);
  const [groupMessageSmsSearchFilter, setGroupMessageSmsSearchFilter] = useLocalStorage(LocalStorageKeys.GroupMessageSmsSearchFilter, {
    sortCol: 'id',
    sortDir: 'desc',
    pageIndex: DEFAULT_PAGE_INDEX,
    pageSize: DEFAULT_PAGE_SIZE,
  } as unknown as GroupMessageSmsSearchFilter);

  const searchFilter: GroupMessageSmsSearchFilter = useMemo(() => {
    return {
      filter: groupMessageSmsSearchFilter?.filter,
      isSent: groupMessageSmsSearchFilter?.isSent,
      activeTab: groupMessageSmsSearchFilter?.activeTab,
      pageSize: groupMessageSmsSearchFilter?.pageSize,
      pageIndex: groupMessageSmsSearchFilter?.pageIndex,
      sortCol: groupMessageSmsSearchFilter?.sortCol,
      sortDir: groupMessageSmsSearchFilter?.sortDir,
    } as GroupMessageSmsSearchFilter;
  }, [
    groupMessageSmsSearchFilter?.activeTab,
    groupMessageSmsSearchFilter?.isSent,
    groupMessageSmsSearchFilter?.pageIndex,
    groupMessageSmsSearchFilter?.pageSize,
    groupMessageSmsSearchFilter?.filter,
    groupMessageSmsSearchFilter?.sortCol,
    groupMessageSmsSearchFilter?.sortDir,
  ]);

  const loadingCallback = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  useEffect(() => {
    if (isMounted() && searchInitialized) {
      if (tempSearchString === '') {
        setGroupMessageSmsSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
      if (tempSearchString && tempSearchString.length >= 2) {
        setGroupMessageSmsSearchFilter((prev) => ({ ...prev, ...{ filter: tempSearchString, pageIndex: 0 } }));
      } else {
        setGroupMessageSmsSearchFilter((prev) => ({ ...prev, ...{ filter: '', pageIndex: 0 } }));
      }
    }
  }, [isMounted, searchInitialized, setGroupMessageSmsSearchFilter, tempSearchString]);

  return (
    <>
      <PageFilterSection
        leftContent={
          <SearchField
            initialValue={searchFilter.filter || ''}
            onSearch={(newSearchString) => {
              setSearchInitialized(true);
              setTempSearchString(newSearchString);
            }}
          />
        }
      />
      <GroupMessageSmsTabs
        initialValue={searchFilter.activeTab}
        callback={(newActiveTab, newIsSent) => {
          setGroupMessageSmsSearchFilter((prev) => ({
            ...prev,
            ...{
              pageIndex: 0,
              activeTab: newActiveTab,
              isSent: newIsSent,
            },
          }));
        }}
      />
      <Divider />
      <GroupMessageSmsGrid
        loadingCallback={loadingCallback}
        searchFilter={searchFilter}
        searchFilterCallback={(newFilter) =>
          setGroupMessageSmsSearchFilter((prev) => ({
            ...prev,
            ...newFilter,
          }))
        }
      />
    </>
  );
};
